import { getProductRechargeData } from '../global/subscription-helpers.js'

export class MitoqStickyAddToCart {
  constructor() {
    this.stickyAddToCart = document.querySelector('[data-sticky-add-to-cart]')
    if (!this.stickyAddToCart) return

    // Get product data from a global or data attribute
    this.product = MitoqStickyAddToCart.getProductFromLiquid()
    this.variants = this.product ? this.product.variants : []
    this.options = this.product ? this.product.options : []
    // Use data-is-b2b attribute for B2B detection
    this.isB2B = this.stickyAddToCart.getAttribute('data-is-b2b') === 'true'
    this.variantIdFromUrl = MitoqStickyAddToCart.getVariantIdFromUrl()

    // Identify subscription and one-time variants
    this.subscriptionVariants = this.variants.filter((v) =>
      v.title.startsWith('SUBS')
    )
    this.onetimeVariants = this.variants.filter((v) =>
      v.title.startsWith('ONCE')
    )

    // For B2B users, filter to only show allowed one-time variants
    this.b2bAllowedOnetimeVariants = this.isB2B
      ? this.getB2BAllowedVariants()
      : this.onetimeVariants

    // Initial method: subscription if available, else one-time
    this.currentMethod =
      this.subscriptionVariants.length > 0 ? 'subscription' : 'onetime'
    this.currentVariant = null

    this.formattedPrices = null // Will hold Recharge formattedPrices

    this.init()
    this.initStickyVisibility()

    this.shouldBeVisible = false // Track if sticky should be visible
    this.footerInView = false // Track if footer is in view
    this.initFooterObserver()

    // Add event listener to sticky add to cart button
    const stickyAddBtn = this.stickyAddToCart.querySelector(
      '.sticky-add-to-cart__add'
    )
    if (stickyAddBtn) {
      stickyAddBtn.addEventListener('click', () => {
        // Find the main product form submit button
        const submitBtn = document.querySelector(
          '.form .product-form__buttons button[type="submit"]'
        )
        if (submitBtn) {
          submitBtn.click()
        }
      })
    }
  }

  static getProductFromLiquid() {
    // Try to get product data from a script tag or data attribute if available
    const el = document.querySelector('[data-product-json]')
    if (el) {
      try {
        return JSON.parse(el.textContent)
      } catch (e) {
        // ignore JSON parse errors
      }
    }
    return null
  }

  static getVariantIdFromUrl() {
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get('variant')
  }

  getB2BAllowedVariants() {
    // Define allowed variant names for B2B users
    const allowedVariantNames = ['ONCE-1M']

    return this.onetimeVariants.filter((variant) =>
      allowedVariantNames.some((allowedName) => variant.title === allowedName)
    )
  }

  async init() {
    // Handle B2B or no subscription variants case
    if (this.isB2B || this.subscriptionVariants.length === 0) {
      await this.handleB2BOrNoSubscriptionCase()
      return
    }

    // Determine initial method and variant for regular case
    const { method, variantId } = this.determineInitialMethodAndVariant()
    this.currentMethod = method
    this.stickyAddToCart.setAttribute('data-purchase-type', method)

    // Load subscription data if needed
    if (method === 'subscription' && !this.formattedPrices) {
      await this.loadSubscriptionData()
    }

    // Render UI
    this.renderVariantButtons(method, variantId)
    this.updatePurchaseTypeActiveClass()
    if (variantId) {
      this.selectVariantButton(variantId)
    }

    // Set up event listeners
    this.setupMethodButtonListeners()
    this.setupSyncEventListener()
  }

  async handleB2BOrNoSubscriptionCase() {
    this.currentMethod = 'onetime'
    this.stickyAddToCart.setAttribute('data-purchase-type', 'onetime')

    // Hide subscription-related UI elements
    const subscribeBtn = this.stickyAddToCart.querySelector(
      '[data-subscribe-button]'
    )
    if (subscribeBtn) subscribeBtn.style.display = 'none'

    const subPriceDl = this.stickyAddToCart.querySelector(
      'dl[data-subscription-price]'
    )
    if (subPriceDl) subPriceDl.style.display = 'none'

    // Use appropriate variant list based on user type
    const availableVariants = this.isB2B
      ? this.b2bAllowedOnetimeVariants
      : this.onetimeVariants

    // Find initial one-time variant
    let initialOneTimeVariantId = null
    if (this.variantIdFromUrl) {
      const match = availableVariants.find(
        (v) => String(v.id) === String(this.variantIdFromUrl)
      )
      if (match) initialOneTimeVariantId = match.id
    }
    if (!initialOneTimeVariantId && availableVariants.length > 0) {
      initialOneTimeVariantId = availableVariants[0].id
    }

    this.renderVariantButtons('onetime', initialOneTimeVariantId)
    this.updatePurchaseTypeActiveClass()
    if (initialOneTimeVariantId) {
      this.selectVariantButton(initialOneTimeVariantId)
    }
  }

  determineInitialMethodAndVariant() {
    let initialMethod = null
    let initialMethodVariantId = null

    // Check if URL variant matches subscription or one-time
    if (this.variantIdFromUrl) {
      const subMatch = this.subscriptionVariants.find(
        (v) => String(v.id) === String(this.variantIdFromUrl)
      )
      const availableOnetimeVariants = this.isB2B
        ? this.b2bAllowedOnetimeVariants
        : this.onetimeVariants
      const oneMatch = availableOnetimeVariants.find(
        (v) => String(v.id) === String(this.variantIdFromUrl)
      )

      if (subMatch) {
        initialMethod = 'subscription'
        initialMethodVariantId = subMatch.id
      } else if (oneMatch) {
        initialMethod = 'onetime'
        initialMethodVariantId = oneMatch.id
      }
    }

    // Fallback to default method if no URL variant found
    if (!initialMethod) {
      initialMethod =
        this.subscriptionVariants.length > 0 ? 'subscription' : 'onetime'

      if (
        initialMethod === 'subscription' &&
        this.subscriptionVariants.length > 0
      ) {
        initialMethodVariantId = this.subscriptionVariants[0].id
      } else if (initialMethod === 'onetime') {
        const availableOnetimeVariants = this.isB2B
          ? this.b2bAllowedOnetimeVariants
          : this.onetimeVariants
        if (availableOnetimeVariants.length > 0) {
          initialMethodVariantId = availableOnetimeVariants[0].id
        }
      }
    }

    return { method: initialMethod, variantId: initialMethodVariantId }
  }

  async loadSubscriptionData() {
    const rechargeData = await getProductRechargeData()
    this.formattedPrices =
      rechargeData && rechargeData.formattedPrices
        ? rechargeData.formattedPrices
        : null
  }

  setupMethodButtonListeners() {
    const subscribeBtn = this.stickyAddToCart.querySelector(
      '[data-subscribe-button]'
    )
    const onetimeBtn = this.stickyAddToCart.querySelector(
      '[data-onetime-button]'
    )

    if (subscribeBtn) {
      subscribeBtn.addEventListener('click', async () => {
        if (this.currentMethod !== 'subscription') {
          this.currentMethod = 'subscription'
          this.stickyAddToCart.setAttribute(
            'data-purchase-type',
            'subscription'
          )

          if (!this.formattedPrices) {
            await this.loadSubscriptionData()
          }

          this.renderVariantButtons('subscription')
          this.updatePurchaseTypeActiveClass()
          if (this.subscriptionVariants.length > 0) {
            this.selectVariantButton(this.subscriptionVariants[0].id)
          }
          this.dispatchSyncEvent()
        }
      })
    }

    if (onetimeBtn) {
      onetimeBtn.addEventListener('click', () => {
        if (this.currentMethod !== 'onetime') {
          this.currentMethod = 'onetime'
          this.stickyAddToCart.setAttribute('data-purchase-type', 'onetime')
          this.renderVariantButtons('onetime')
          this.updatePurchaseTypeActiveClass()
          const availableOnetimeVariants = this.isB2B
            ? this.b2bAllowedOnetimeVariants
            : this.onetimeVariants
          if (availableOnetimeVariants.length > 0) {
            this.selectVariantButton(availableOnetimeVariants[0].id)
          }
          this.dispatchSyncEvent()
        }
      })
    }
  }

  setupSyncEventListener() {
    window.addEventListener('mitoq-subscription-sync', async (e) => {
      const { method, variantId } = e.detail

      if (method && method !== this.currentMethod) {
        this.currentMethod = method
        this.stickyAddToCart.setAttribute('data-purchase-type', method)

        if (method === 'subscription' && !this.formattedPrices) {
          await this.loadSubscriptionData()
        }

        this.renderVariantButtons(method, variantId)
        this.updatePurchaseTypeActiveClass()

        if (variantId) {
          this.selectVariantButton(variantId)
        } else if (
          method === 'subscription' &&
          this.subscriptionVariants.length > 0
        ) {
          this.selectVariantButton(this.subscriptionVariants[0].id)
        } else if (method === 'onetime') {
          const availableOnetimeVariants = this.isB2B
            ? this.b2bAllowedOnetimeVariants
            : this.onetimeVariants
          if (availableOnetimeVariants.length > 0) {
            this.selectVariantButton(availableOnetimeVariants[0].id)
          }
        }
      } else if (variantId) {
        this.selectVariantButton(variantId)
      }
    })
  }

  updatePurchaseTypeActiveClass() {
    const subscribeBtn = this.stickyAddToCart.querySelector(
      '[data-subscribe-button]'
    )
    const onetimeBtn = this.stickyAddToCart.querySelector(
      '[data-onetime-button]'
    )
    if (!subscribeBtn || !onetimeBtn) return
    if (this.currentMethod === 'subscription') {
      subscribeBtn.classList.add('is-active')
      onetimeBtn.classList.remove('is-active')
    } else {
      subscribeBtn.classList.remove('is-active')
      onetimeBtn.classList.add('is-active')
    }
  }

  renderVariantButtons(method, selectVariantId) {
    const container = this.stickyAddToCart.querySelector('.all-variant-buttons')
    if (!container) return
    container.innerHTML = ''
    let variants
    if (method === 'subscription') {
      variants = this.subscriptionVariants
    } else if (this.isB2B) {
      variants = this.b2bAllowedOnetimeVariants
    } else {
      variants = this.onetimeVariants
    }
    if (!variants.length) return
    variants.forEach((variant, idx) => {
      const btn = document.createElement('button')
      btn.setAttribute('type', 'button')
      btn.setAttribute('data-variant-id', variant.id)
      btn.setAttribute('data-selected', 'false')
      btn.innerHTML = `<span>${MitoqStickyAddToCart.getDisplayName(variant)}</span>`
      btn.addEventListener('click', () => {
        this.selectVariantButton(variant.id)
        this.dispatchSyncEvent()
      })
      container.appendChild(btn)
      // Select initial variant
      if (
        (selectVariantId && String(variant.id) === String(selectVariantId)) ||
        (!selectVariantId && idx === 0)
      ) {
        this.selectVariantButton(variant.id)
      }
    })
  }

  static getDisplayName(variant) {
    // Use metafield or fallback to title parsing
    if (
      variant.metafields &&
      variant.metafields.mitoq &&
      variant.metafields.mitoq.display_name
    ) {
      return variant.metafields.mitoq.display_name
    }
    // Fallback: parse from title (e.g., SUBS-1M => 1-month supply)
    const match = variant.title.match(/-(\d+)M/i)
    if (match) {
      const months = match[1]
      return `${months}-month supply`
    }
    return variant.title
  }

  selectVariantButton(variantId) {
    const container = this.stickyAddToCart.querySelector('.all-variant-buttons')
    if (!container) return
    const buttons = container.querySelectorAll('button[data-variant-id]')
    buttons.forEach((btn) => {
      if (String(btn.getAttribute('data-variant-id')) === String(variantId)) {
        btn.setAttribute('data-selected', 'true')
        this.currentVariant = variantId
        this.updatePrice(variantId)
      } else {
        btn.setAttribute('data-selected', 'false')
      }
    })
  }

  updatePrice(variantId) {
    // Find the variant object
    const variant = this.variants.find(
      (v) => String(v.id) === String(variantId)
    )
    if (!variant) {
      return
    }

    // Elements
    const priceContainer = this.stickyAddToCart.querySelector(
      '.sticky-add-to-cart__price'
    )
    const onetimePriceDl = priceContainer.querySelector(
      'dl[data-onetime-price]'
    )
    const subscriptionPriceDl = priceContainer.querySelector(
      'dl[data-subscription-price]'
    )

    // Clear both
    onetimePriceDl.innerHTML = ''
    subscriptionPriceDl.innerHTML = ''

    // Currency
    const currencyCode =
      (this.product && this.product.currency) ||
      (this.stickyAddToCart &&
        this.stickyAddToCart.getAttribute('data-currency')) ||
      (window.Shopify && Shopify.currency && Shopify.currency.active) ||
      ''
    const currencySymbol =
      (this.product && this.product.currency_symbol) ||
      (this.stickyAddToCart &&
        this.stickyAddToCart.getAttribute('data-currency-symbol')) ||
      '$'

    // Helper to format price
    const formatPrice = (price) => {
      let num = typeof price === 'number' ? price : parseFloat(price)
      if (!Number.isNaN(num)) {
        if (Number.isInteger(num) && num >= 100) num /= 100
        return num.toFixed(2)
      }
      return ''
    }

    // Helper to split price string into symbol and value
    const splitPriceString = (priceStr) => {
      if (typeof priceStr !== 'string')
        return { symbol: currencySymbol, value: formatPrice(priceStr) }
      const match = priceStr.match(/([^\d.,]+)?([\d.,]+)/)
      if (match) {
        return { symbol: match[1] || currencySymbol, value: match[2] }
      }
      return { symbol: currencySymbol, value: priceStr }
    }

    // Helper to split price into symbol and value for one-time
    const splitPriceNumber = (num) => {
      // Always use currencySymbol for one-time
      return { symbol: currencySymbol, value: formatPrice(num) }
    }

    if (
      this.currentMethod === 'subscription' &&
      this.formattedPrices &&
      this.formattedPrices[variantId]
    ) {
      // Subscription: use Recharge formattedPrices
      const priceData = this.formattedPrices[variantId]
      const original = priceData.unit
      const { discounted } = priceData
      // Determine if there is a discount
      let hasDiscount = false
      if (priceData.sellingPlans) {
        for (const planId of Object.keys(priceData.sellingPlans)) {
          const plan = priceData.sellingPlans[planId]
          if (plan.discountValue > 0) {
            hasDiscount = true
            break
          }
        }
      }
      // Set data-has-discount attribute for styling
      this.stickyAddToCart.setAttribute(
        'data-has-discount',
        hasDiscount ? 'true' : 'false'
      )
      // Render original and discounted price
      const { symbol: origSymbol, value: origValue } =
        splitPriceString(original)
      const { symbol: discSymbol, value: discValue } =
        splitPriceString(discounted)

      // If currency is EUR, replace decimal point with comma in origValue and discValue
      const origValueFormatted = MitoqStickyAddToCart.formatPriceForEUR(
        currencyCode,
        origValue
      )
      const discValueFormatted = MitoqStickyAddToCart.formatPriceForEUR(
        currencyCode,
        discValue
      )

      const originalHtml = `<span>${origSymbol}</span><span>${origValueFormatted}</span><span> ${currencyCode}</span>`
      const discountedHtml = hasDiscount
        ? `<span>${discSymbol}</span><span>${discValueFormatted}</span><span> ${currencyCode}</span>`
        : ''
      const html = `
        <dt class="visually-hidden">Original Price:</dt>
        <dd data-original-price data-subscription-original-price>${originalHtml}</dd>
        <dt class="visually-hidden">Discounted Price:</dt>
        <dd data-discounted-price data-subscription-discounted-price>${discountedHtml}</dd>
      `
      subscriptionPriceDl.innerHTML = html
    } else {
      // One-time: use price and compare_at_price for discount
      const { price } = variant
      const compare = variant.compare_at_price
      const hasDiscount = compare && Number(compare) > Number(price)
      this.stickyAddToCart.setAttribute(
        'data-has-discount',
        hasDiscount ? 'true' : 'false'
      )
      const { symbol: origSymbol, value: origValue } = hasDiscount
        ? splitPriceNumber(compare)
        : splitPriceNumber(price)
      const { symbol: discSymbol, value: discValue } = splitPriceNumber(price)

      // If currency is EUR, replace decimal point with comma in origValue and discValue
      const origValueFormatted = MitoqStickyAddToCart.formatPriceForEUR(
        currencyCode,
        origValue
      )
      const discValueFormatted = MitoqStickyAddToCart.formatPriceForEUR(
        currencyCode,
        discValue
      )

      const originalHtml = origValue
        ? `<span>${origSymbol}</span><span>${origValueFormatted}</span><span> ${currencyCode}</span>`
        : ''
      const discountedHtml = hasDiscount
        ? `<span>${discSymbol}</span><span>${discValueFormatted}</span><span> ${currencyCode}</span>`
        : ''
      const html = `
        <dt class="visually-hidden">Original Price:</dt>
        <dd data-original-price data-onetime-original-price>${originalHtml}</dd>
        <dt class="visually-hidden">Discounted Price:</dt>
        <dd data-discounted-price data-onetime-discounted-price>${discountedHtml}</dd>
      `
      onetimePriceDl.innerHTML = html
    }
  }

  static formatPriceForEUR(currencyCode, price) {
    if (currencyCode === 'EUR') {
      return price.replace('.', ',')
    }
    return price
  }

  dispatchSyncEvent() {
    // Dispatch event to sync with main widget
    const event = new CustomEvent('mitoq-sticky-atc-sync', {
      detail: {
        method: this.currentMethod,
        variantId: this.currentVariant,
      },
    })
    window.dispatchEvent(event)
  }

  initStickyVisibility() {
    // Set initial state: hidden
    this.stickyAddToCart.style.transform = 'translateY(100%)'
    this.stickyAddToCart.classList.remove('is-visible')
    // Get the top fold element
    const topFold = document.querySelector('.section-main-product')
    // Listen for scroll
    window.addEventListener('scroll', () => {
      if (!topFold) return
      const rect = topFold.getBoundingClientRect()
      // Show sticky ATC when the bottom of .section-main-product enters the viewport
      if (rect.bottom + 800 <= window.innerHeight) {
        this.shouldBeVisible = true
        if (!this.footerInView) {
          this.stickyAddToCart.classList.add('is-visible')
          this.stickyAddToCart.style.transform = 'translateY(0)'
        } else {
          this.stickyAddToCart.classList.remove('is-visible')
          this.stickyAddToCart.style.transform = 'translateY(100%)'
        }
      } else {
        this.shouldBeVisible = false
        this.stickyAddToCart.classList.remove('is-visible')
        this.stickyAddToCart.style.transform = 'translateY(100%)'
      }
    })
  }

  initFooterObserver() {
    const footer = document.querySelector('.footer')
    if (!footer) {
      return
    }
    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.footerInView = true
            this.stickyAddToCart.classList.remove('is-visible')
            this.stickyAddToCart.style.transform = 'translateY(100%)'
          }
          // Only show if it should be visible (e.g., scrolled past topFold)
          else if (this.shouldBeVisible) {
            this.footerInView = false
            this.stickyAddToCart.classList.add('is-visible')
            this.stickyAddToCart.style.transform = 'translateY(0)'
          } else {
            this.footerInView = false
            this.stickyAddToCart.classList.remove('is-visible')
            this.stickyAddToCart.style.transform = 'translateY(100%)'
          }
        })
      },
      {
        root: null,
        threshold: 0,
      }
    )
    observer.observe(footer)
  }
}
